import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import '../styles/TestModule.css';
import testService from '../services/testService';
import leaksService from '../services/leaksService';
import surveyService from '../services/surveyService';
import authService from '../services/authService';
import MasterEdLogo from '../assets/masteredlogo-removebg-preview.png';
import congratsImage from '../assets/congrats_image.png';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { getWeekKey, isDismissedForWeek, dismissForWeek, isSnoozed, snoozeHours } from '../utils/surveyReminder';
import presenceService, { PresenceStatus } from '../services/presenceService';
import { processMathContent } from '../utils/mathRenderer';
const API_IMAGE_BASE = process.env.REACT_APP_API_URL || '';

const TestModule = ({ userData, isLeaksMode = false }) => {
  const studentFullName = userData?.fullName || localStorage.getItem('student_name') || 'Test User';
  const { moduleType: urlModuleType, moduleNumber, sessionId: leaksSessionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const testId = queryParams.get('testId');
  
  // Determine if this is a Leaks test
  const isLeaks = isLeaksMode || !!leaksSessionId;
  const currentSessionId = leaksSessionId || null;
  
  // Fix #2: Use sessionId for Leaks progress key, testId for weekly tests
  const progressKey = isLeaks ? `leaksProgress:${currentSessionId}` : `testProgress:${testId}`;
  
  
  const [test, setTest] = useState(null);
  const [testSession, setTestSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  
  const [activeModuleType, setActiveModuleType] = useState('verbal');
  const [timeLeft, setTimeLeft] = useState(10); 
  const [showTimer, setShowTimer] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [questionAnswers, setQuestionAnswers] = useState({});
  const [totalQuestions, setTotalQuestions] = useState(0); 
  const [showQuestionNav, setShowQuestionNav] = useState(false);
  const dropdownRef = React.useRef(null);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [reviewQuestions, setReviewQuestions] = useState([]);
  const [showReviewPage, setShowReviewPage] = useState(false);
  const [currentModule, setCurrentModule] = useState(1); 
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);
  const [transitionMessage, setTransitionMessage] = useState('');
  const [showBreakScreen, setShowBreakScreen] = useState(false);
  const [breakTimeLeft, setBreakTimeLeft] = useState(10 * 60); 
  const [showCompletionScreen, setShowCompletionScreen] = useState(false);
  const [showSurveyAfterTest, setShowSurveyAfterTest] = useState(false);
  const [surveyWeekKey, setSurveyWeekKey] = useState(null);

  const [showExitModal, setShowExitModal] = useState(false);
const [openImageModal, setOpenImageModal] = useState(false);
const [modalImageSrc, setModalImageSrc] = useState('');
  const exitListenerRef = useRef(null);

  const [showResumeModal, setShowResumeModal] = useState(false);
  const [savedProgress, setSavedProgress] = useState(null);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [resultStats, setResultStats] = useState({ correctCount: 0, incorrectCount: 0, score: 0 });
  
  const [showCalculator, setShowCalculator] = useState(false);
  const calculatorRef = useRef(null);
  const calculatorInstance = useRef(null);
  const [calcPos, setCalcPos] = useState({ x: 20, y: 80 });
  const calcContainerRef = useRef(null);
  const [calcSide, setCalcSide] = useState('left');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  const [isTouch, setIsTouch] = useState(false);
  const [calcDims, setCalcDims] = useState({ width: 600, height: 400 });
  const passageRef = useRef(null);
  const [highlightMode, setHighlightMode] = useState(false);
  const [passageHighlights, setPassageHighlights] = useState({});
  const lastSelectionRef = useRef({ range: null, ts: 0 });

  
  const currentQuestionData = questions[currentQuestion - 1] || {};
  const currentOptions = ['A','B','C','D']
    .map(id => ({ id, text: currentQuestionData[`option${id}`] }))
    .filter(opt => opt.text);

  const testContentStyle = React.useMemo(() => {
    if (!showCalculator || isMobile) return undefined;
    const gutter = 16;
    const containerWidth = (calcContainerRef.current?.offsetWidth) || (calcDims.width + 20);
    if (calcSide === 'left') {
      // Content starts to the right of calculator's right edge
      const leftMargin = Math.min(window.innerWidth, Math.max(0, calcPos.x + containerWidth + gutter));
      return { marginLeft: `${leftMargin}px` };
    }
    // Content ends to the left of calculator's left edge
    const rightMargin = Math.min(window.innerWidth, Math.max(0, (window.innerWidth - calcPos.x) + gutter));
    return { marginRight: `${rightMargin}px` };
  }, [showCalculator, isMobile, calcSide, calcDims, calcPos]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // -------- Passage Highlight: Helpers & Handlers --------
  const isNodeInPassage = (node) => passageRef.current && node && passageRef.current.contains(node);

  const applyHighlightFromSelection = () => {
    const sel = window.getSelection ? window.getSelection() : null;
    if (!sel || sel.isCollapsed) return;
    if (sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const { startContainer, endContainer } = range;
    if (!isNodeInPassage(startContainer) || !isNodeInPassage(endContainer)) return;
    try {
      const wrapper = document.createElement('span');
      wrapper.className = 'passage-highlight';
      const frag = range.extractContents();
      wrapper.appendChild(frag);
      range.insertNode(wrapper);
      // Do not force-clear selection; letting it persist avoids perceived flicker on mobile
      // Persist updated HTML
      const html = passageRef.current?.innerHTML || '';
      setPassageHighlights((prev) => ({ ...prev, [currentQuestionData.id]: html }));
      // Clear saved selection snapshot after applying highlight
      lastSelectionRef.current = { range: null, ts: 0 };
    } catch {}
  };

  const handlePassageMouseUp = () => {
    if (!highlightMode) return;
    setTimeout(applyHighlightFromSelection, 0);
  };

  const handlePassageTouchEnd = () => {
    if (!highlightMode) return;
    setTimeout(applyHighlightFromSelection, 0);
  };

  const handlePassageClick = (e) => {
    if (!highlightMode) return;
    const span = e.target && e.target.closest ? e.target.closest('.passage-highlight') : null;
    if (span && passageRef.current && passageRef.current.contains(span)) {
      const parent = span.parentNode;
      while (span.firstChild) parent.insertBefore(span.firstChild, span);
      parent.removeChild(span);
      const html = passageRef.current.innerHTML;
      setPassageHighlights((prev) => ({ ...prev, [currentQuestionData.id]: html }));
    }
  };

  const clearAllHighlightsForPassage = () => {
    if (!passageRef.current) return;
    const container = passageRef.current;
    const spans = container.querySelectorAll('span.passage-highlight');
    spans.forEach((span) => {
      const parent = span.parentNode;
      while (span.firstChild) parent.insertBefore(span.firstChild, span);
      parent.removeChild(span);
    });
    const html = container.innerHTML;
    setPassageHighlights((prev) => ({ ...prev, [currentQuestionData.id]: html }));
  };
  
  // Keep passage HTML unmanaged by React to preserve selection during rerenders (e.g., timer tick)
  const currentPassageHtml = React.useMemo(() => {
    return (passageHighlights[currentQuestionData.id] ?? currentQuestionData.passageText) || '';
  }, [passageHighlights, currentQuestionData.id, currentQuestionData.passageText]);

  useEffect(() => {
    const el = passageRef.current;
    if (!el) return;
    if (el.innerHTML !== currentPassageHtml) {
      el.innerHTML = currentPassageHtml;
    }
  }, [currentPassageHtml, currentQuestionData.id]);

  // Attach native listeners in highlight mode only to avoid React-driven selection resets
  useEffect(() => {
    const el = passageRef.current;
    if (!el) return;

    const onMouseUp = () => { if (highlightMode) setTimeout(applyHighlightFromSelection, 0); };
    const onTouchEnd = () => { if (highlightMode) setTimeout(applyHighlightFromSelection, 0); };
    const onClick = (e) => {
      if (!highlightMode) return;
      const span = e.target && e.target.closest ? e.target.closest('.passage-highlight') : null;
      if (span && el.contains(span)) {
        const parent = span.parentNode;
        while (span.firstChild) parent.insertBefore(span.firstChild, span);
        parent.removeChild(span);
        const html = el.innerHTML;
        setPassageHighlights((prev) => ({ ...prev, [currentQuestionData.id]: html }));
      }
    };
    const onSelectStart = (e) => {
      // Ensure selection is allowed within passage; stop other handlers from cancelling
      if (el.contains(e.target)) {
        e.stopPropagation();
      }
    };
    const onSelectionChange = () => {
      if (!highlightMode) return;
      const sel = window.getSelection ? window.getSelection() : null;
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) return;
      const r = sel.getRangeAt(0);
      if (!isNodeInPassage(r.startContainer) || !isNodeInPassage(r.endContainer)) return;
      try {
        lastSelectionRef.current = { range: r.cloneRange(), ts: Date.now() };
      } catch {}
    };

    if (highlightMode) {
      el.addEventListener('mouseup', onMouseUp);
      el.addEventListener('touchend', onTouchEnd, { passive: true });
      el.addEventListener('click', onClick);
      // Capture-phase listeners to preempt global blockers
      document.addEventListener('selectstart', onSelectStart, true);
      document.addEventListener('selectionchange', onSelectionChange);
    }

    return () => {
      el.removeEventListener('mouseup', onMouseUp);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('click', onClick);
      document.removeEventListener('selectstart', onSelectStart, true);
      document.removeEventListener('selectionchange', onSelectionChange);
    };
  }, [highlightMode, currentQuestionData.id]);

  // Guard: briefly restore a fresh selection inside passage if it collapses unexpectedly (e.g., due to rerender)
  useEffect(() => {
    if (!highlightMode) return;
    const id = window.setInterval(() => {
      try {
        const sel = window.getSelection ? window.getSelection() : null;
        const snap = lastSelectionRef.current;
        if (!sel || !snap || !snap.range) return;
        const withinWindow = Date.now() - (snap.ts || 0) < 1500; // 1.5s window
        if (!withinWindow) return;
        if (sel.isCollapsed) {
          sel.removeAllRanges();
          sel.addRange(snap.range);
        }
      } catch {}
    }, 250);
    return () => window.clearInterval(id);
  }, [highlightMode]);
  
  
  const getHeaderText = () => {
    const hasVerbal = !!(testSession?.questions?.some(q => q.questionType === 'Verbal'));
    const sectionNumber = activeModuleType === 'verbal' ? 1 : (hasVerbal ? 2 : 1);
    if (activeModuleType === 'verbal') {
      return `${test?.name || 'Test'} | Section ${sectionNumber}, Module ${currentModule}: R & W`;
    } else if (activeModuleType === 'math') {
      return `${test?.name || 'Test'} | Section ${sectionNumber}, Module ${currentModule === 1 ? '1: Math' : '2: Math'}`;
    }
    return `${test?.name || 'Test'} | Module ${currentModule}`;
  };

  
  useEffect(() => {
    const loadTestData = async () => {
      // For Leaks mode, we need sessionId; for weekly tests, we need testId
      if (!testId && !currentSessionId) {
        setError('No test ID or session ID provided');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        let sessionData, testData;
        
        if (isLeaks && currentSessionId) {
          // Leaks mode: Session already started, just get questions via start endpoint
          const leaksTestId = testId || queryParams.get('leaksTestId');
          
          if (!leaksTestId) {
            setError('Leaks test ID not found');
            setLoading(false);
            return;
          }
          
          // Call start endpoint - backend returns existing session if already started
          const response = await leaksService.startTest(parseInt(leaksTestId));
          sessionData = response.data;
          testData = { id: sessionData.testId, name: sessionData.testName };
        } else if (testId) {
          // Weekly test mode: Use original flow
          sessionData = await testService.startTest(testId);
          testData = await testService.getTestById(testId);
        }
        
        setTestSession(sessionData);
        setTest(testData);

        
        if (sessionData.questions && sessionData.questions.length > 0) {
          const verbalQs = sessionData.questions.filter(q => q.questionType === 'Verbal');
          const mathQs = sessionData.questions.filter(q => q.questionType === 'Math');
          if (verbalQs.length > 0) {
            setActiveModuleType('verbal');
            setQuestions(verbalQs);
            setTotalQuestions(verbalQs.length);
            setTimeLeft(verbalQs.length === 1 ? 30 : 32 * 60);
          } else if (mathQs.length > 0) {
            setActiveModuleType('math');
            setQuestions(mathQs);
            setTotalQuestions(mathQs.length);
            setTimeLeft(mathQs.length === 1 ? 30 : 35 * 60);
          } else {
            setError('No questions found in this test');
          }
        } else {
          setError('No questions found in this test');
        }
      } catch (err) {
        // SECURITY: Check if test was already submitted
        if (err.response && err.response.status === 400) {
          if (err.response.data && err.response.data.submitted) {
            // Weekly test already submitted
            console.warn('Test already submitted, redirecting to results...');
            alert('This test has already been submitted and cannot be retaken. Redirecting to results page.');
            navigate(`/detailed-results/${testId}`, { replace: true });
            return;
          }
          if (err.response.data && err.response.data.error && err.response.data.error.includes('already completed')) {
            // Leaks test already completed
            console.warn('Leaks test already completed');
            alert('This test has already been completed. You can only take each test once.');
            navigate('/leaks', { replace: true });
            return;
          }
        }
        console.error('Error loading test:', err);
        setError('Failed to load test. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    
    if (!authService.isLoggedIn()) {
      navigate('/login');
      return;
    }
    
    loadTestData();
  }, [testId, currentSessionId, isLeaks, navigate]);

  // Presence heartbeat: start when test data is loaded, adapt on visibility, cleanup on unmount
  useEffect(() => {
    if (!testSession || !test || showCompletionScreen) return;
    const ctxProvider = () => ({
      testId: testSession?.id || testSession?.testId || (testId ? parseInt(testId, 10) : null),
      testName: test?.name || null,
    });
    presenceService.startAutoHeartbeat(PresenceStatus.InTest, ctxProvider);

    const onVisibility = () => {
      if (document.hidden) {
        presenceService.setStatus(PresenceStatus.Idle);
      } else if (!showBreakScreen && !showCompletionScreen) {
        presenceService.setStatus(PresenceStatus.InTest);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const onPageHide = () => {
      presenceService.setStatus(PresenceStatus.Idle);
    };
    window.addEventListener('pagehide', onPageHide);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      presenceService.stopAutoHeartbeat();
    };
  }, [testSession, test, showCompletionScreen, showBreakScreen, testId]);

  // Hide any existing Tawk widget entirely while on the test page
  useEffect(() => {
    try { document.body.classList.add('no-tawk'); } catch {}
    return () => {
      try { document.body.classList.remove('no-tawk'); } catch {}
    };
  }, []);

  useEffect(() => {
    if (!showBreakScreen) return;
    
    const breakTimer = setInterval(() => {
      setBreakTimeLeft(prevTime => {
        if (prevTime <= 0) {
          clearInterval(breakTimer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(breakTimer);
  }, [showBreakScreen]);
  
  // Presence: reflect break state in status
  useEffect(() => {
    if (!testSession) return;
    if (showBreakScreen) {
      presenceService.setStatus(PresenceStatus.Idle);
    } else if (!showCompletionScreen) {
      presenceService.setStatus(PresenceStatus.InTest);
    }
  }, [showBreakScreen, showCompletionScreen, testSession]);
  
  
  

  useEffect(() => {
  }, [activeModuleType, currentModule, showBreakScreen, showTransitionScreen]);

  // When entering highlight mode, bring the passage into view
  useEffect(() => {
    if (highlightMode && passageRef.current) {
      try { passageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch {}
    }
  }, [highlightMode, currentQuestion]);

  // Disable highlight mode outside verbal
  useEffect(() => {
    if (activeModuleType !== 'verbal' && highlightMode) setHighlightMode(false);
  }, [activeModuleType]);

  // Viewport detection, calc sizing, and position clamping
  useEffect(() => {
    const recompute = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const mobile = w < 768;
      const tablet = w >= 768 && w < 1200;
      const desktop = w >= 1200;
      setIsMobile(mobile);
      setIsTablet(tablet);
      setIsDesktop(desktop);
      const touch = 'ontouchstart' in window || (navigator && navigator.maxTouchPoints > 0);
      setIsTouch(!!touch);

      const newDims = desktop
        ? { width: 600, height: 400 }
        : tablet
          ? { width: Math.min(460, Math.max(360, Math.floor(w * 0.44))), height: 320 }
          : { width: 360, height: 300 };
      setCalcDims(prev => (prev.width === newDims.width && prev.height === newDims.height) ? prev : newDims);

      // Clamp calculator position within viewport
      setCalcPos(prev => {
        const containerWidth = (calcContainerRef.current?.offsetWidth) || (newDims.width + 20);
        const containerHeight = (calcContainerRef.current?.offsetHeight) || (newDims.height + 44);
        const headerH = document.querySelector('.test-header')?.offsetHeight || 0;
        const footerH = document.querySelector('.test-footer')?.offsetHeight || 0;
        const minX = 0;
        const minY = headerH;
        const maxX = Math.max(minX, w - containerWidth);
        const maxY = Math.max(minY, h - footerH - containerHeight);
        const nx = Math.min(Math.max(prev.x, minX), maxX);
        const ny = Math.min(Math.max(prev.y, minY), maxY);
        if (nx !== prev.x || ny !== prev.y) {
          return { x: nx, y: ny };
        }
        return prev;
      });
    };
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, []);


  
  
  useEffect(() => {
    if (!showCalculator) return;
    // Ensure Desmos CSS is present so expressions + keypad render correctly
    if (!document.querySelector('link[data-desmos-css]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://www.desmos.com/api/v1.11/calculator.css';
      link.setAttribute('data-desmos-css', 'true');
      document.head.appendChild(link);
    }
    const initCalculator = () => {
      if (calculatorRef.current && !calculatorInstance.current) {
        calculatorInstance.current = window.Desmos.GraphingCalculator(calculatorRef.current, {
          expressions: true,              // show expression input fields
          expressionsCollapsed: false,    // keep them visible by default
          expressionsTopbar: true,        // show + and options bar
          settingsMenu: false,
          zoomButtons: true,
          keypad: true,                   // always show on-screen keypad
        });
        // Ensure there is at least one editable row
        try { calculatorInstance.current.setExpression({ id: 'expr0', latex: '' }); } catch {}
        // Force keypad open explicitly for non-touch laptops
        try {
          const st = calculatorInstance.current.getState();
          calculatorInstance.current.setState({ ...st, keypadOpen: true });
        } catch {}
      }
      if (calculatorInstance.current) {
        try {
          calculatorInstance.current.updateSettings({ keypad: true });
          calculatorInstance.current.resize();
          // Keep keypad open after resize
          try {
            const st2 = calculatorInstance.current.getState();
            calculatorInstance.current.setState({ ...st2, keypadOpen: true });
          } catch {}
        } catch {}
      }
    };
    if (!window.Desmos) {
      const script = document.createElement('script');
      script.src = 'https://www.desmos.com/api/v1.11/calculator.js?apiKey=dcb31709b452b1cf9dc26972add0fda6';
      script.async = true;
      script.onload = initCalculator;
      document.body.appendChild(script);
    } else {
      initCalculator();
    }
  }, [showCalculator, calcDims, isTablet, isTouch]);

  
  const handleCalcMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const initX = calcPos.x;
    const initY = calcPos.y;
    const onMouseMove = (e) => {
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      const containerWidth = (calcContainerRef.current?.offsetWidth) || (calcDims.width + 20);
      const containerHeight = (calcContainerRef.current?.offsetHeight) || (calcDims.height + 44);
      const headerH = document.querySelector('.test-header')?.offsetHeight || 0;
      const footerH = document.querySelector('.test-footer')?.offsetHeight || 0;
      const minX = 0;
      const minY = headerH;
      const maxX = Math.max(minX, window.innerWidth - containerWidth);
      const maxY = Math.max(minY, window.innerHeight - footerH - containerHeight);
      const nx = Math.min(Math.max(initX + dx, minX), maxX);
      const ny = Math.min(Math.max(initY + dy, minY), maxY);
      setCalcPos({ x: nx, y: ny });
      const midX = nx + containerWidth / 2;
      setCalcSide(midX < window.innerWidth / 2 ? 'left' : 'right');
    };
    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };  

  
  const handleCalcTouchStart = (e) => {
    e.preventDefault();
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    const initX = calcPos.x;
    const initY = calcPos.y;
    const onTouchMove = (e) => {
      e.preventDefault();
      const touchMove = e.touches[0];
      const dx = touchMove.clientX - startX;
      const dy = touchMove.clientY - startY;
      const containerWidth = (calcContainerRef.current?.offsetWidth) || (calcDims.width + 20);
      const containerHeight = (calcContainerRef.current?.offsetHeight) || (calcDims.height + 44);
      const headerH = document.querySelector('.test-header')?.offsetHeight || 0;
      const footerH = document.querySelector('.test-footer')?.offsetHeight || 0;
      const minX = 0;
      const minY = headerH;
      const maxX = Math.max(minX, window.innerWidth - containerWidth);
      const maxY = Math.max(minY, window.innerHeight - footerH - containerHeight);
      const nx = Math.min(Math.max(initX + dx, minX), maxX);
      const ny = Math.min(Math.max(initY + dy, minY), maxY);
      setCalcPos({ x: nx, y: ny });
      const midX = nx + containerWidth / 2;
      setCalcSide(midX < window.innerWidth / 2 ? 'left' : 'right');
    };
    const onTouchEnd = () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd);
  };

  // Close calculator automatically on phones
  useEffect(() => {
    if (isMobile && showCalculator) {
      try { calculatorInstance.current?.destroy(); } catch {}
      calculatorInstance.current = null;
      setShowCalculator(false);
    }
  }, [isMobile, showCalculator]);

  
  useEffect(() => {
    if (timeLeft <= 0) {
      
      if (activeModuleType === 'verbal' && currentModule === 1) {
        
        if (totalQuestions <= 27) {
          
          const mathQs = (testSession?.questions || []).filter(q => q.questionType === 'Math');
          if (mathQs.length > 0) {
            resumeTesting();
          } else {
            completeModule();
          }
          return;
        }
        
        setShowTransitionScreen(true);
        setTransitionMessage('This Module Is Over');
        setTimeout(() => {
          setShowTransitionScreen(false);
          setCurrentModule(2);
          setShowReviewPage(false);
          setCurrentQuestion(1);
          setTimeLeft(totalQuestions === 1 ? 30 : 32 * 60);
          setQuestionAnswers({});
          setAnsweredQuestions([]);
          setReviewQuestions([]);
        }, 5000);
      
      } else if (activeModuleType === 'verbal' && currentModule === 2) {
        
        setShowTransitionScreen(true);
        setTransitionMessage('This Module Is Over');
        
        
        setTimeout(() => {
          setShowTransitionScreen(false);
          setShowBreakScreen(true);
        }, 5000);
      
      } else if (activeModuleType === 'math' && currentModule === 1) {
        
        if (totalQuestions <= 22) {
          completeModule();
          return;
        }
        
        setShowTransitionScreen(true);
        setTransitionMessage('This Module Is Over');
        setTimeout(() => {
          setShowTransitionScreen(false);
          setCurrentModule(2);
          setShowReviewPage(false);
          setCurrentQuestion(1);
          setTimeLeft(testSession.questions.filter(q => q.questionType === 'Math').length === 1 ? 30 : 35 * 60);
          setQuestionAnswers({});
          setAnsweredQuestions([]);
          setReviewQuestions([]);
        }, 5000);
      
      } else if (activeModuleType === 'math' && currentModule === 2) {
        completeModule();
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prevTime => prevTime - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft, currentModule]);

  const handleNextQuestion = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    } else if (currentQuestion === totalQuestions) {
      
      setShowReviewPage(true);
    }
  };

  const handlePrevQuestion = () => {
    if (showReviewPage) {
      
      setShowReviewPage(false);
      setCurrentQuestion(totalQuestions);
    } else if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const goToQuestion = (questionNum) => {
    setCurrentQuestion(questionNum);
    setShowQuestionNav(false);
    setShowReviewPage(false);
  };
  
  
  const handleAnswerSelect = async (optionId) => {
    
    const question = questions[currentQuestion - 1];
    
    // For LEAKS tests, convert option letter to option text
    let answerToSave = optionId;
    if (isLeaks && ['A', 'B', 'C', 'D'].includes(optionId)) {
      // optionId is a letter (A/B/C/D), get the actual text
      answerToSave = question[`option${optionId}`];
    }
    
    const updatedAnswers = { ...questionAnswers, [question.id]: optionId };
    setQuestionAnswers(updatedAnswers);
    
    
    if (!answeredQuestions.includes(question.id)) {
      setAnsweredQuestions([...answeredQuestions, question.id]);
    }
    
    
    if (testSession && testSession.id && questions[currentQuestion - 1]) {
      try {
        const question = questions[currentQuestion - 1];
        // Use appropriate service based on test type
        if (isLeaks) {
          await leaksService.submitAnswer(currentSessionId, {
            questionId: question.id,
            studentAnswer: answerToSave  // Send option text, not letter
          });
        } else {
          await testService.submitAnswer(
            testSession.id,
            question.id,
            optionId
          );
        }
      } catch (err) {
        console.error('Error submitting answer:', err);
      }
    }
  };

  
  const handleOpenAnswerChange = async (e) => {
    let val = e.target.value;
    
    val = val.replace(/[^0-9.\-]/g, '');
    
    val = val.replace(/(?!^)-/g, '');
    
    val = val.replace(/(\..*)\./g, '$1.');
    
    if (val.startsWith('-')) {
      val = val.slice(0, 6);
    } else {
      val = val.slice(0, 5);
    }
    setQuestionAnswers(prev => ({ ...prev, [currentQuestionData.id]: val }));
    
    if (val !== '') {
      setAnsweredQuestions(prevAnswered => prevAnswered.includes(currentQuestionData.id) ? prevAnswered : [...prevAnswered, currentQuestionData.id]);
    } else {
      setAnsweredQuestions(prevAnswered => prevAnswered.filter(id => id !== currentQuestionData.id));
    }
    
    // Submit to backend for LEAKS tests
    if (isLeaks && testSession && testSession.id) {
      try {
        await leaksService.submitAnswer(currentSessionId, {
          questionId: currentQuestionData.id,
          studentAnswer: val
        });
      } catch (err) {
        console.error('Error submitting write-in answer:', err);
      }
    }
  };

  const toggleQuestionNav = () => {
    setShowQuestionNav(!showQuestionNav);
  };
  
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowQuestionNav(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  const markForReview = (questionNum) => {
    if (reviewQuestions.includes(questionNum)) {
      setReviewQuestions(reviewQuestions.filter(q => q !== questionNum));
    } else {
      setReviewQuestions([...reviewQuestions, questionNum]);
    }
  };
  
  
  const completeModule = async () => {
    
    const sessionId = testSession?.id || testSession?.testId;
    if (sessionId) {
      try {
        setLoading(true);
        
        // Use appropriate service based on test type
        if (isLeaks) {
          // For Leaks, backend calculates scores (frontend doesn't have correct answers)
          const response = await leaksService.completeTest(currentSessionId);
          
          // Use backend-calculated results
          const backendResults = response.data?.results;
          if (backendResults) {
            const correct = (backendResults.readingWriting?.correct || 0) + (backendResults.math?.correct || 0);
            const total = (backendResults.readingWriting?.total || 0) + (backendResults.math?.total || 0);
            const incorrect = total - correct;
            
            setResultStats({ correctCount: correct, incorrectCount: incorrect, score: 0 });
          } else {
            setResultStats({ correctCount: 0, incorrectCount: 0, score: 0 });
          }
        } else {
          // For weekly tests, calculate locally (frontend has correct answers)
          let correct = 0, incorrect = 0, totalWrongPoints = 0;
          const allQuestions = testSession.questions || [];
          allQuestions.forEach((q) => {
            const answer = questionAnswers[q.id];
            // Only count if question was actually answered
            if (answer !== undefined && answer !== null && answer !== '') {
              if (answer === q.correctAnswer) {
                correct++;
              } else {
                incorrect++;
                totalWrongPoints += q.points || 0;
              }
            }
            // Unanswered questions are NOT counted as correct or incorrect
          });
          const score = 1600 - totalWrongPoints;
          
          const stats = { correctCount: correct, incorrectCount: incorrect, score };
          setResultStats(stats);
          
          const answersPayload = (testSession.questions || []).map(q => ({
            questionId: q.id,
            questionNumber: q.questionNumber,
            answer: questionAnswers[q.id],
            isCorrect: questionAnswers[q.id] === q.correctAnswer
          }));
          
          await testService.completeTest(sessionId, stats, answersPayload);
        }
        
        // SECURITY: Clear saved progress after successful submission to prevent re-entry
        try {
          localStorage.removeItem(progressKey);
        } catch (err) {
          console.warn('Failed to clear progress:', err);
        }
        
        setShowTransitionScreen(false);
        setShowBreakScreen(false);
        setShowCompletionScreen(true);
        // Presence: mark Idle and stop heartbeats after completion
        try {
          await presenceService.setStatus(PresenceStatus.Idle);
        } catch {}
        presenceService.stopAutoHeartbeat();
        // After completion, nudge to complete weekly survey if pending
        try {
          const studentPkStr = localStorage.getItem('student_pk');
          const sid = studentPkStr ? parseInt(studentPkStr, 10) : null;
          if (sid) {
            const status = await surveyService.getStatus(sid);
            const pending = !(status?.submittedThisWeek || status?.SubmittedThisWeek);
            const wk = getWeekKey(status?.weekStart || status?.WeekStart);
            setSurveyWeekKey(wk || null);
            if (pending && !isSnoozed() && !isDismissedForWeek(wk)) {
              setShowSurveyAfterTest(true);
            }
          }
        } catch {}
      } catch (err) {
        setError('Failed to submit your test. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    

  };

  
  const formatBreakTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  
  const resumeTesting = () => {
    setShowBreakScreen(false);
    setActiveModuleType('math');
    setCurrentModule(1);
    setCurrentQuestion(1);
    setTimeLeft(testSession.questions.filter(q => q.questionType === 'Math').length === 1 ? 30 : 35 * 60);
    const mathQs = testSession.questions.filter(q => q.questionType === 'Math');
    setQuestions(mathQs);
    setTotalQuestions(mathQs.length);
    setReviewQuestions([]);
    setShowReviewPage(false);
    // Presence: back to in-test when resuming
    presenceService.setStatus(PresenceStatus.InTest);
  };

  
  useEffect(() => {
    if (breakTimeLeft === 0 && showBreakScreen) {
      const autoTimer = setTimeout(() => resumeTesting(), 30000);
      return () => clearTimeout(autoTimer);
    }
  }, [breakTimeLeft, showBreakScreen]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = 'Your progress is autosaved and will be restored when you return.'; 
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (!testSession) return;
    const raw = localStorage.getItem(progressKey);
    if (raw) {
      const data = JSON.parse(raw);
      setSavedProgress(data);
      setShowResumeModal(true);
    }
  }, [testSession]);

  useEffect(() => {
    if (!testSession) return;
    const data = { questionAnswers, answeredQuestions, currentQuestion, timeLeft, activeModuleType, currentModule, reviewQuestions, passageHighlights };
    localStorage.setItem(progressKey, JSON.stringify(data));
  }, [questionAnswers, answeredQuestions, currentQuestion, timeLeft, activeModuleType, currentModule, reviewQuestions, passageHighlights]);

  useEffect(() => {
    if (showCompletionScreen) localStorage.removeItem(progressKey);
  }, [showCompletionScreen]);
  const resumeProgress = () => {
    setQuestionAnswers(savedProgress?.questionAnswers || {});
    setAnsweredQuestions(savedProgress?.answeredQuestions || []);
    setCurrentQuestion(savedProgress?.currentQuestion || 1);
    setTimeLeft(savedProgress?.timeLeft || timeLeft);
    setActiveModuleType(savedProgress?.activeModuleType || activeModuleType);
    setCurrentModule(savedProgress?.currentModule || currentModule);

    const moduleQs = savedProgress?.activeModuleType === 'math'
      ? testSession.questions.filter(q => q.questionType === 'Math')
      : testSession.questions.filter(q => q.questionType === 'Verbal');
    setQuestions(moduleQs);
    setTotalQuestions(moduleQs.length);

    setReviewQuestions(savedProgress?.reviewQuestions || []);
    setPassageHighlights(savedProgress?.passageHighlights || {});
    setShowResumeModal(false);
  };

  const discardProgress = () => {
    localStorage.removeItem(progressKey);
    setShowResumeModal(false);
  };

  const handleSubmit = () => {
    // Open styled confirmation modal instead of native confirm
    setShowSubmitModal(true);
  };

  const doSubmitAction = () => {
    // Close modal and proceed with original submit logic
    setShowSubmitModal(false);
    if (activeModuleType === 'verbal') {
      const mathQs = testSession?.questions?.filter(q => q.questionType === 'Math') || [];
      if (mathQs.length > 0) {
        resumeTesting();
      } else {
        completeModule();
      }
    } else {
      completeModule();
    }
  };

  useEffect(() => {
    exitListenerRef.current = () => {
      setShowExitModal(true);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', exitListenerRef.current);
    return () => {
      window.removeEventListener('popstate', exitListenerRef.current);
    };
  }, []);

  const confirmExit = () => {
    window.removeEventListener('popstate', exitListenerRef.current);
    navigate(-1);
  };

  const cancelExit = () => {
  window.history.pushState(null, '', window.location.href);
  setShowExitModal(false);
};

const handleOpenImage = (src) => {
  setModalImageSrc(src);
  setOpenImageModal(true);
};

const handleCloseImage = () => {
  setOpenImageModal(false);
};

// Return JSX follows

  return (
    <div className="test-module-container">
            <Dialog open={showResumeModal} onClose={resumeProgress}>
        <DialogTitle>Resume Test</DialogTitle>
        <DialogContent>We detected an unfinished test session. Would you like to resume where you left off?</DialogContent>
        <DialogActions>
          
          <Button onClick={resumeProgress} color="primary">Resume</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showExitModal} onClose={cancelExit}>
        <DialogTitle>Exit Test</DialogTitle>
        <DialogContent>Are you sure you want to exit? Your progress is saved.</DialogContent>
        <DialogActions>
          <Button onClick={cancelExit}>Cancel</Button>
          <Button onClick={confirmExit} color="error">Exit</Button>
        </DialogActions>
      </Dialog>

      {/* Survey nudge after completion */}
      <Dialog open={showSurveyAfterTest} onClose={() => setShowSurveyAfterTest(false)}>
        <DialogTitle>One more step: Weekly Survey</DialogTitle>
        <DialogContent>
          Great job finishing your test! Please take 20 seconds to complete this week's teacher survey.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { snoozeHours(6); setShowSurveyAfterTest(false); }}>Remind me later</Button>
          <Button onClick={() => { try { if (surveyWeekKey) dismissForWeek(surveyWeekKey); } catch {} setShowSurveyAfterTest(false); }}>Don't remind this week</Button>
          <Button onClick={() => { setShowSurveyAfterTest(false); navigate('/survey'); }} variant="contained">Complete now</Button>
        </DialogActions>
      </Dialog>
      
      <Dialog open={showSubmitModal} onClose={() => setShowSubmitModal(false)}>
        <DialogTitle>Submit Test</DialogTitle>
        <DialogContent>Are you sure you want to submit your test?</DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSubmitModal(false)}>Cancel</Button>
          <Button onClick={doSubmitAction} color="primary" variant="contained">{(activeModuleType === 'verbal' && ((testSession?.questions?.filter(q => q.questionType === 'Math') || []).length > 0)) ? 'Continue' : 'Submit'}</Button>
        </DialogActions>
      </Dialog>
      <img src={MasterEdLogo} alt="MasterEd Logo" className="mastered-logo" />
      {}
      {!showCompletionScreen && showTransitionScreen && (
        <div className="transition-screen">
          <h1 className="transition-title">{transitionMessage}</h1>
          <p className="transition-message">All your work has been saved.</p>
          <p className="transition-message">You'll move on automatically in just a moment.</p>
          <p className="transition-message">Do not refresh this page or quit the app.</p>
          <div className="transition-loader"></div>
        </div>
      )}

      {}
      {!showCompletionScreen && showBreakScreen && (
        <div className="break-screen">
          <div className="break-content">
            <div className="break-timer-column">
              <div className="break-timer-container">
                <p className="break-timer-label">Remaining Break Time:</p>
                <div className="break-timer">{formatBreakTime(breakTimeLeft)}</div>
              </div>
              <button className="resume-testing-btn" onClick={resumeTesting}>Skip Break</button>
            </div>
            
            <div className="break-instructions">
              <h2>Take a Break: Do Not Close Your Device</h2>
              <p>After the break, you'll start the next section.</p>
              <p>When the timer reaches zero, you'll automatically move on.</p>
              <p><strong>Please stay seated and remain quiet.</strong></p>
              
              <ol className="break-rules">
                <li>&nbsp;Do not disturb students who are still testing.</li>
                <li>&nbsp;Do not exit the app or close your laptop.</li>
                <li>&nbsp;Do not access phones, smartwatches, textbooks, notes, or the internet.</li>
                <li>&nbsp;Do not eat or drink near any testing device.</li>
                <li>&nbsp;Do not speak in the testing room; outside the room, do not discuss the exam with anyone.</li>
              </ol>
            </div>
          </div>
          
          <div className="break-footer">
            <div className="user-name">{studentFullName}</div>
          </div>
        </div>
      )}
      
      {}
      {showCompletionScreen && (
        <div className="completion-screen">
          <div className="completion-content">
            <h1 className="completion-title">Congratulations!</h1>
            <p className="completion-message">Here are your results:</p>
            <div className="completion-box">
              <div className="completion-image">
                <img src={congratsImage} alt="Congratulations" />
              </div>
              <div className="completion-stats">
                <p><strong>Correct Answers:</strong> {resultStats.correctCount}</p>
                <p><strong>Incorrect Answers:</strong> {resultStats.incorrectCount}</p>
              </div>
            </div>
            <div className="completion-button-group">
              <button className="return-home-btn" onClick={() => navigate(isLeaks ? '/leaks' : '/', { replace: true })}>Return to {isLeaks ? 'Past Papers' : 'Homepage'}</button>
              <button className="detailed-results-btn" onClick={() => navigate(isLeaks ? `/leaks/results/${currentSessionId}` : `/detailed-results/${testId}`, { replace: true })}>Check Detailed Results</button>
            </div>
          </div>
        </div>
      )}

      {}
      {!showTransitionScreen && !showBreakScreen && !showCompletionScreen && (
        <div className="test-main-wrapper">
          <header className="test-header">
        <div className="header-left">
          <div className="section-info">
            <span>{getHeaderText()}</span>
          </div>
        </div>

      <div className="timer-wrapper">
        <div className={`timer ${showTimer ? '' : 'timer-hidden'}`}>{formatTime(timeLeft)}</div>
        <button
          className="hide-timer-button"
          onClick={() => setShowTimer(prev => !prev)}
        >
          {showTimer ? 'Hide' : 'Show'}
        </button>
      </div>
      <div className="tools">
        {activeModuleType === 'math' && !isMobile && (
          <button
            className="tool-button"
            onClick={() => {
              if (showCalculator) {
                try { calculatorInstance.current?.destroy(); } catch {}
                calculatorInstance.current = null;
                setShowCalculator(false);
              } else {
                // Initialize sensible starting position
                const headerH = document.querySelector('.test-header')?.offsetHeight || 0;
                const footerH = document.querySelector('.test-footer')?.offsetHeight || 0;
                const containerWidth = (calcContainerRef.current?.offsetWidth) || (calcDims.width + 20);
                const containerHeight = (calcContainerRef.current?.offsetHeight) || (calcDims.height + 44);
                const x = isTablet ? Math.max(0, window.innerWidth - containerWidth - 20) : 20;
                const minY = headerH + 10;
                const maxY = Math.max(minY, window.innerHeight - footerH - containerHeight - 10);
                const y = Math.min(Math.max(80, minY), maxY);
                setCalcPos({ x, y });
                setCalcSide(isTablet ? 'right' : 'left');
                setShowCalculator(true);
              }
            }}
          >
            Calculator
          </button>
        )}
        {activeModuleType === 'math' && (
          <button
            className="tool-button"
            onClick={() => handleOpenImage('/SAT-Math-Formulas1.png')}
          >
            Reference
          </button>
        )}
        {activeModuleType === 'verbal' && (
          <>
            <button
              className={`tool-button ${highlightMode ? 'active' : ''}`}
              onClick={() => setHighlightMode((v) => !v)}
            >
              {highlightMode ? 'Exit Highlight' : 'Highlight'}
            </button>
            {highlightMode && (
              <button className="tool-button" onClick={clearAllHighlightsForPassage}>
                Clear Highlights
              </button>
            )}
          </>
        )}
      </div>
    </header>

    <main className="test-content" style={testContentStyle}>
      {!showReviewPage && (
        <>
          {activeModuleType === 'verbal' ? (
            <>
                <div className="reading-section">
  {currentQuestionData.imageUrl && (
    <div className="verbal-image-container">
      <img
  src={API_IMAGE_BASE + currentQuestionData.imageUrl}
  alt="Question image"
  style={{ cursor: 'zoom-in', maxWidth: '100%' }}
  onClick={() => handleOpenImage(`${API_IMAGE_BASE}${currentQuestionData.imageUrl}`)}
/>
    </div>
  )}
                  {/* Display Leaks passage in left section */}
                  {currentQuestionData.passage ? (
                    <div className="leaks-passage-text" dangerouslySetInnerHTML={{ __html: currentQuestionData.passage }} />
                  ) : (
                    <div
                      ref={passageRef}
                      className={`passage-text ${highlightMode ? 'highlight-mode' : ''}`}
                    />
                  )}
                  {highlightMode && (
                    <div className="highlight-hint">Highlight mode: select text to highlight. Click a highlight to remove.</div>
                  )}
                </div>
                <div className="question-section">

                  <div className="question-header">
                    <div className="question-number-box">
                      <span className="question-label">{currentQuestion}</span>
                    </div>
                    <button className="mark-review-button" onClick={() => {
                      if (reviewQuestions.includes(currentQuestion)) {
                        setReviewQuestions(reviewQuestions.filter(q => q !== currentQuestion));
                      } else {
                        setReviewQuestions([...reviewQuestions, currentQuestion]);
                      }
                    }}>
                      <span className="review-icon">{reviewQuestions.includes(currentQuestion) ? '☑' : '☐'}</span>
                      Mark for Review
                    </button>
                  </div>
                  {/* Display question text */}
                  {currentQuestionData.text && (
                    <div className="question-text">
                      <p>{currentQuestionData.text}</p>
                    </div>
                  )}
                  <div className="answer-options">
                    {currentOptions.length > 0 ? (
                      currentOptions.map((option) => {
                        const isSelected = questionAnswers[currentQuestionData.id] === option.id;
                        return (
                          <button 
                            key={option.id} 
                            className={`answer-option ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleAnswerSelect(option.id)}
                          >
                            <div className="option-letter">{option.id}</div>
                            <span className="option-text">
                              {option.text && option.text.includes('<') ? (
                                <span dangerouslySetInnerHTML={{ __html: option.text }} />
                              ) : (
                                option.text
                              )}
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <input
                        type="text"
                        className="open-answer-input"
                        placeholder="Enter numeric answer"
                        value={questionAnswers[currentQuestionData.id] || ''}
                        onChange={handleOpenAnswerChange}/>
                    )}
                </div>
              </div>
            
            </>

            ) : (
              <div key={`q-${currentQuestionData.id}`} className="math-question-container">
                <div className="question-header">
                  <div className="question-number-box">
                    <span className="question-label">{currentQuestion}</span>
                  </div>
                  <button className="mark-review-button" onClick={() => {
                    if (reviewQuestions.includes(currentQuestion)) {
                      setReviewQuestions(reviewQuestions.filter(q => q !== currentQuestion));
                    } else {
                      setReviewQuestions([...reviewQuestions, currentQuestion]);
                    }
                  }}>
                    <span className="review-icon">{reviewQuestions.includes(currentQuestion) ? '☑' : '☐'}</span>
                    Mark for Review
                  </button>
                </div>
                <div className="math-question-content">
                  {}
                  {currentQuestionData.imageUrl && (
                    <div className="math-graph-container">
                      <img
  key={currentQuestionData.imageUrl}
  src={API_IMAGE_BASE + currentQuestionData.imageUrl}
  alt="Math question graph"
  style={{ cursor: 'zoom-in', maxWidth: '100%' }}
  onClick={() => handleOpenImage(`${API_IMAGE_BASE}${currentQuestionData.imageUrl}`)}
/>
                    </div>
                  )}
                  {/* Display passage/article if available (Leaks format with images) */}
                  {currentQuestionData.passage && (
                    <div className="question-passage" dangerouslySetInnerHTML={{ __html: processMathContent(currentQuestionData.passage) }} />
                  )}
                  {/* Display question text */}
                  {currentQuestionData.text && (
                    <div className="question-text">
                      <div dangerouslySetInnerHTML={{ __html: processMathContent(currentQuestionData.text) }} />
                    </div>
                  )}
                  <div className="answer-options">
                    {currentOptions.length > 0 ? (
                      currentOptions.map((option) => {
                        const isSelected = questionAnswers[currentQuestionData.id] === option.id;
                        return (
                          <button 
                            key={option.id} 
                            className={`answer-option ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleAnswerSelect(option.id)}
                          >
                            <div className="option-letter">{option.id}</div>
                            <span className="option-text">
                              {option.text && option.text.includes('<') ? (
                                <span dangerouslySetInnerHTML={{ __html: processMathContent(option.text) }} />
                              ) : (
                                <InlineMath math={option.text || ''} />
                              )}
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <input
                        type="text"
                        className="open-answer-input"
                        placeholder="Enter numeric answer"
                        value={questionAnswers[currentQuestionData.id] || ''}
                        onChange={handleOpenAnswerChange}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
        </>
          )}
      {showReviewPage && ( 
          <div className="review-page">
            <div className="review-page-content">
              <h2>Check Your Work</h2>
              <p>Look over your work and check any questions you marked for review.</p>
              <p>When the timer reaches zero, you'll automatically move on.</p>
              <p><strong>Please stay seated and remain quiet.</strong></p>
              
              <div className="review-section">
                <div className="review-section-header">
                  <h3 className="review-section-title">Section {activeModuleType === 'verbal' ? 'I' : 'II'}, Module {currentModule}: {activeModuleType === 'verbal' ? 'Reading and Writing' : 'Math'} Questions</h3>
                  
                  <div className="review-status-indicators">
                    <div className="status-indicator">
                      <div className="status-icon unanswered"></div>
                      <span>Unanswered</span>
                    </div>
                    <div className="status-indicator">
                      <div className="status-icon for-review"></div>
                      <span>For Review</span>
                    </div>
                  </div>
                </div>
                
                <hr className="review-divider" />
                
                <div className="review-questions-grid">
                  {Array.from({ length: totalQuestions }, (_, i) => i + 1).map(num => {
                    const question = questions[num - 1] || {};
                    const isAnswered = answeredQuestions.includes(question.id);
                    const isForReview = reviewQuestions.includes(num);
                    const classes = `review-question-box ${isAnswered ? 'answered' : ''} ${isForReview ? 'for-review' : ''}`;
                    
                    return (
                      <div 
                        key={num} 
                        className={classes}
                        onClick={() => {
                          setCurrentQuestion(num);
                          setShowReviewPage(false);
                        }}
                      >
                        <span>{num}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
          {showCalculator && (
            <div
              ref={calcContainerRef}
              style={{
                position: 'fixed',
                left: calcPos.x,
                top: calcPos.y,
                width: `${calcDims.width + 20}px`,
                background: '#fff',
                border: '1px solid #ccc',
                zIndex: 100,
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}
            >
              <div
                onMouseDown={handleCalcMouseDown}
                onTouchStart={handleCalcTouchStart}
                style={{
                  touchAction: 'none',
                  cursor: 'move',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: '#f0f0f0',
                  padding: '4px 8px',
                  borderBottom: '1px solid #ccc'
                }}
              >
                <span>Calculator</span>
                <button
                  style={{ border: 'none', background: 'none', fontSize: '16px', cursor: 'pointer' }}
                  onClick={() => {
                    try { calculatorInstance.current?.destroy(); } catch {}
                    calculatorInstance.current = null;
                    setShowCalculator(false);
                  }}
                >
                  ×
                </button>
              </div>
              <div ref={calculatorRef} style={{ width: `${calcDims.width}px`, height: `${calcDims.height}px` }} />
            </div>
          )}



      <Dialog open={openImageModal} onClose={handleCloseImage} fullWidth maxWidth="lg">
  <DialogContent dividers>
    <Box component="img" src={modalImageSrc} alt="Full-size image" sx={{ width: '100%', height: 'auto' }} />
  </DialogContent>
  <DialogActions>
    <Button onClick={handleCloseImage}>Close</Button>
  </DialogActions>
</Dialog>

<footer className="test-footer">
        <div className="user-info">
          <span className="user-name">{studentFullName}</span>
        </div>
        {!showReviewPage && (
          <div className="question-selector" ref={dropdownRef}>
            <button className="question-selector-button" onClick={toggleQuestionNav}>
              Question {currentQuestion} of {totalQuestions} <span className={`dropdown-arrow ${showQuestionNav ? 'up' : 'down'}`}>{showQuestionNav ? '▲' : '▼'}</span>
            </button>
            {showQuestionNav && (
              <div className="question-dropdown">
                <div className="modal-content">
                  <div className="modal-header">
                    <h2>{activeModuleType === 'verbal' ? 'Section I, Module ' + currentModule + ': Reading and Writing Questions' : 'Section II, Module ' + currentModule + ': Math Questions'}</h2>
                    <button className="close-modal" onClick={toggleQuestionNav}>✕</button>
                  </div>
                  <hr />
                  <div className="legend">
                    <div className="legend-item">
                      <span className="legend-icon current">⦿</span> Current
                    </div>
                    <div className="legend-item">
                      <span className="legend-icon unanswered">□</span> Unanswered
                    </div>
                    <div className="legend-item">
                      <span className="legend-icon review">⚑</span> For Review
                    </div>
                  </div>
                  <hr />
                  <div className="questions-grid">
                    {Array.from({ length: totalQuestions }, (_, i) => i + 1).map(num => {
                      const question = questions[num - 1] || {};
                      const isCurrent = num === currentQuestion;
                      const isCompleted = answeredQuestions.includes(question.id);
                      const isForReview = reviewQuestions.includes(num);
                      
                      let className = 'question-grid-item';
                      if (isCurrent) className += ' current';
                      if (isCompleted) className += ' completed';
                      if (isForReview) className += ' for-review';
                      
                      return (
                        <button 
                          key={num} 
                          className={className} 
                          onClick={() => goToQuestion(num)}
                        >
                          <span className="question-number">{num}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="modal-footer">
                    <button 
                      className="review-page-button" 
                      onClick={() => {
                        setShowReviewPage(true);
                        setShowQuestionNav(false);
                      }}
                    >
                      Go to Review Page
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="question-navigation">
          {(currentQuestion > 1 || showReviewPage) && (
            <button className="nav-button back" onClick={handlePrevQuestion}>
              Back
            </button>
          )}
          {(!showReviewPage && currentQuestion < totalQuestions) && (
            <button className="nav-button next" onClick={handleNextQuestion}>
              Next
            </button>
          )}
          {(showReviewPage || currentQuestion === totalQuestions) && (
            <button className="nav-button submit" onClick={handleSubmit}>
              Submit
            </button>
          )}
        </div>
      </footer>
        </div>
      )}
    </div>
  );
};

export default TestModule;
